/**
 * What a failed request throws, on every surface.
 *
 * `ApiError` carries the status, which is what almost every caller branches on. Its
 * subclass adds the `/v1` envelope's machine-readable `code` — branch on that rather
 * than on the message text, which is prose and will be reworded.
 *
 * The class hierarchy lives here, in the package, because both apps and the shared
 * clients raise and catch it. Which app is running decides how a failure is REPORTED
 * (a dashboard navigates on a 401, a side panel writes a sentence); it does not decide
 * what a failure IS.
 */

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

/** The v1 error body: `{"error": {"code", "message", "details"}}`. `code` is a
 *  stable snake_case string and is the thing to branch on; `message` is human and
 *  may change freely; `details` is always an object. */
export interface V1ErrorBody {
  code: string;
  message: string;
  details: Record<string, unknown>;
}

/**
 * A v1 failure, as a typed error.
 *
 * It extends `ApiError` deliberately. `handle401` is the single 401/402 policy for
 * the whole admin app and is called at 78 sites; it branches on
 * `err instanceof ApiError`, on `err.status`, and — for 402 only — on
 * `err.message === 'payment_required'`. Nothing about that may change while both
 * surfaces are live, so:
 *
 *   * `status` is the HTTP status, exactly as before;
 *   * `message` is the server's human message, EXCEPT on a 402, where it is the
 *     stable `code`. That is not a special case so much as a guarantee: the legacy
 *     402 detail was already the machine string (`api/deps.py` raises
 *     `_reject(request, 402, "payment_required")`), and pinning it to `code` means
 *     the trial-vs-paywall branch cannot be broken by someone rewording a message.
 *   * `code` and `details` are the new, structured half — use them for anything
 *     v1-specific, never `message`.
 */
export class CodedApiError extends ApiError {
  readonly code: string;
  readonly details: Record<string, unknown>;

  constructor(status: number, code: string, message: string, details: Record<string, unknown> = {}) {
    super(status, status === 402 ? code : message);
    this.name = 'CodedApiError';
    this.code = code;
    this.details = details;
  }
}

export function isCodedError(err: unknown): err is CodedApiError {
  return err instanceof CodedApiError;
}

/**
 * True when a community-scoped request failed BECAUSE of the community id, not
 * because of what was asked for.
 *
 * This is the recovery predicate for doc 04 §4.1 design point 2: the page fetches
 * at RTT 1 against the id it read out of `localStorage`, and if that id is stale
 * the server says so and the page retries against the bootstrap's server-resolved
 * `selected_skool_id`. `withCommunity` (`lib/community.svelte.ts`) is the wrapper;
 * this is the test it uses.
 *
 * A plain `ApiError` (a page still on a legacy route) qualifies only at 403 — the
 * legacy scope answer. 404 is not included there because on `/admin/*` it means
 * "no such row" far more often than "no such community".
 */
export function isStaleCommunity(err: unknown): boolean {
  if (err instanceof CodedApiError) {
    return err.code === 'community_not_in_scope' || err.code === 'community_not_found';
  }
  return err instanceof ApiError && err.status === 403;
}
